import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Same TR phone helpers as Register.jsx (kept local to avoid new module).
const TR_PHONE_RE = /^(90)?0?5\d{9}$/;
const normPhone = (raw) => {
  const s = (raw || "").replace(/\D/g, "");
  if (!s) return "";
  if (s.startsWith("90") && s.length === 12) return "+" + s;
  if (s.startsWith("0") && s.length === 11) return "+90" + s.slice(1);
  if (s.length === 10 && s.startsWith("5")) return "+90" + s;
  return raw.trim();
};

export default function ProfilePage() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", surname: "", phone: "", email: "" });
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const [emailChanged, setEmailChanged] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        surname: user.surname || "",
        phone: user.phone || "",
        email: user.email || "",
      });
    }
  }, [user]);

  if (!user) {
    return <div className="mx-auto max-w-md px-4 py-10 text-sm text-muted-foreground">Giriş yapmalısınız.</div>;
  }

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const updPwd = (k) => (e) => setPwd((p) => ({ ...p, [k]: e.target?.value ?? e }));

  const saveProfile = async (e) => {
    e.preventDefault();
    const phoneNorm = form.phone ? normPhone(form.phone) : "";
    if (phoneNorm && !TR_PHONE_RE.test(phoneNorm.replace("+", ""))) {
      toast.error("Geçerli bir Türkiye cep telefonu giriniz");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", {
        name: form.name.trim(),
        surname: form.surname.trim(),
        phone: phoneNorm,
        email: form.email.trim().toLowerCase(),
      });
      toast.success("Profiliniz güncellendi");
      const changedEmail = data?.user?.email && data.user.email !== user.email;
      if (changedEmail) setEmailChanged(true);
      await refresh();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Güncellenemedi");
    } finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 6) { toast.error("Yeni şifre en az 6 karakter olmalı"); return; }
    if (pwd.next !== pwd.confirm) { toast.error("Yeni şifre ile tekrarı uyuşmuyor"); return; }
    setChangingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Şifreniz güncellendi. Lütfen yeni şifreyle giriş yapın.");
      setPwd({ current: "", next: "", confirm: "" });
      await logout();
      nav("/giris");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Şifre değiştirilemedi");
    } finally { setChangingPwd(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <h1 className="text-2xl font-bold mb-4">Profil Düzenle</h1>

      <form onSubmit={saveProfile} className="rounded-2xl border p-5 space-y-3" data-testid="profile-form">
        <h3 className="font-semibold">Kişisel Bilgiler</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Ad</Label>
            <Input data-testid="profile-name" value={form.name} onChange={upd("name")} className="mt-1 h-11 rounded-xl" required />
          </div>
          <div>
            <Label>Soyad</Label>
            <Input data-testid="profile-surname" value={form.surname} onChange={upd("surname")} className="mt-1 h-11 rounded-xl" />
          </div>
        </div>
        <div>
          <Label>Telefon</Label>
          <Input data-testid="profile-phone" type="tel" inputMode="tel" placeholder="05XX XXX XX XX" value={form.phone} onChange={upd("phone")} className="mt-1 h-11 rounded-xl" />
        </div>
        <div>
          <Label>E-posta</Label>
          <Input data-testid="profile-email" type="email" value={form.email} onChange={upd("email")} className="mt-1 h-11 rounded-xl" required />
          <div className="text-xs text-muted-foreground mt-1">E-postanız login kimliğinizdir. Değiştirdiğinizde bir sonraki girişte yeni e-postayı kullanın.</div>
        </div>
        {emailChanged && (
          <div className="rounded-xl bg-amber-500/10 border border-amber-500/40 text-amber-900 p-3 text-sm">
            E-postanız güncellendi. Aynı hesabınız korunuyor; ancak yeni giriş yaparken yeni e-postayı kullanın.
          </div>
        )}
        <Button type="submit" disabled={saving} className="rounded-full" data-testid="profile-save">
          {saving ? "Kaydediliyor…" : "Bilgileri Kaydet"}
        </Button>
      </form>

      <form onSubmit={changePassword} className="rounded-2xl border p-5 space-y-3 mt-4" data-testid="pwd-form">
        <h3 className="font-semibold">Şifre Değiştir</h3>
        <div>
          <Label>Mevcut Şifre</Label>
          <Input data-testid="pwd-current" type="password" value={pwd.current} onChange={updPwd("current")} className="mt-1 h-11 rounded-xl" required />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Yeni Şifre (min 6)</Label>
            <Input data-testid="pwd-next" type="password" minLength={6} value={pwd.next} onChange={updPwd("next")} className="mt-1 h-11 rounded-xl" required />
          </div>
          <div>
            <Label>Yeni Şifre (Tekrar)</Label>
            <Input data-testid="pwd-confirm" type="password" minLength={6} value={pwd.confirm} onChange={updPwd("confirm")} className="mt-1 h-11 rounded-xl" required />
          </div>
        </div>
        <Button type="submit" disabled={changingPwd} className="rounded-full" data-testid="pwd-submit">
          {changingPwd ? "Değiştiriliyor…" : "Şifreyi Değiştir"}
        </Button>
      </form>

      <div className="rounded-2xl border border-red-200 bg-red-50/60 p-5 mt-4" data-testid="profile-delete-section">
        <h3 className="font-semibold text-red-800">Hesabımı Sil</h3>
        <p className="text-sm text-neutral-700 mt-1">
          Hesabınızı ve kişisel verilerinizi kalıcı olarak silmek için güvenli
          silme sayfasını kullanın. İşlem geri alınamaz.
        </p>
        <Button
          type="button"
          data-testid="profile-open-delete"
          onClick={() => nav("/hesap-silme")}
          className="mt-3 bg-red-600 hover:bg-red-700 text-white rounded-full"
        >
          Hesabımı Sil sayfasını aç
        </Button>
      </div>
    </div>
  );
}
